import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Typography,
  Fade
} from '@mui/material';
import { useTenant } from '../contexts/TenantContext';
import { api } from '../services/api';
import { Space } from '../types';

interface SpaceSelectorProps {
  selectedSpaceId?: string;
  onSpaceChange: (spaceId?: string) => void;
}

const SpaceSelector: React.FC<SpaceSelectorProps> = ({ selectedSpaceId, onSpaceChange }) => {
  const { tenantConfig } = useTenant();
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [loading, setLoading] = useState(true);

  // Only show space selector for multi-space tenants
  const isMultiSpace = tenantConfig?.tenant_type === 'multi_space';

  useEffect(() => {
    const fetchSpaces = async () => {
      if (!isMultiSpace) {
        setLoading(false);
        return;
      }

      try {
        const fetchedSpaces = await api.getSpaces();
        setSpaces(fetchedSpaces);
      } catch (error) {
        console.error('Failed to fetch spaces:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSpaces();
  }, [isMultiSpace]);

  // Don't render anything for single-space tenants
  if (!isMultiSpace || loading) {
    return null;
  }

  // Don't render if no spaces available
  if (spaces.length === 0) {
    return null;
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    // If "All Spaces" tab (value === 'all'), pass undefined to show all ideas
    onSpaceChange(newValue === 'all' ? undefined : newValue);
  };

  // Get current tab value: 'all' if no space selected, otherwise the space ID
  const currentTab = selectedSpaceId || 'all';

  return (
    <Fade in={true} timeout={600}>
      <Box sx={{ 
        mb: 3,
        borderBottom: 1, 
        borderColor: 'divider',
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.1) 20%, rgba(0,0,0,0.1) 80%, transparent)',
        }
      }}>
        <Typography 
          variant="h6" 
          component="h2" 
          sx={{ 
            mb: 2, 
            fontWeight: 500,
            color: 'text.secondary'
          }}
        >
          Filter by Space
        </Typography>
        
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: 'secondary.main',
              height: 3,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500,
              minHeight: 48,
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'secondary.main',
                fontWeight: 600,
              },
              '&:hover': {
                color: 'secondary.dark',
                backgroundColor: 'rgba(0,0,0,0.04)',
              }
            }
          }}
        >
          {/* "All Spaces" tab */}
          <Tab 
            label="All Spaces" 
            value="all" 
            sx={{ 
              mr: 1,
              borderRadius: '8px 8px 0 0',
            }}
          />
          
          {/* Individual space tabs */}
          {spaces.map((space) => (
            <Tab 
              key={space.id} 
              label={space.name} 
              value={space.id}
              sx={{ 
                mr: 1,
                borderRadius: '8px 8px 0 0',
              }}
            />
          ))}
        </Tabs>
      </Box>
    </Fade>
  );
};

export default SpaceSelector; 